import type { RouteLocationRaw, RouteRecordName } from 'vue-router'
import type { Project } from '@/models/Project'

/**
 * Where the saved-prompts pages live (MYAC-1140).
 *
 * They hang off each project type's CHAT route — `/qms/{pid}/chat/prompts` and
 * `/products/{pid}/chat/prompts` — because prompts belong to the chat, and
 * because a feature with records of its own deserves a real URL rather than a
 * modal (Aymeric, 2026-08-28).
 *
 * Only QMS and Products have a chat page. Regulatory-monitoring projects show
 * the prompt picker in the global dock (it is gated on having a project, not on
 * the project's type) but have no chat route to hang a page off, so every
 * helper here returns null for them and the callers disable the control with a
 * reason rather than routing nowhere. `GlobalChatPanel`'s own "expand" control
 * already resolves to nothing on those projects, for exactly the same reason.
 */

export const PROMPT_ROUTES = {
  qms: { list: 'qms-chat-prompts', detail: 'qms-chat-prompt', chat: 'qms-chat' },
  td: { list: 'products-chat-prompts', detail: 'products-chat-prompt', chat: 'products-chat' },
} as const

/** The id the detail route uses for "a prompt that does not exist yet". */
export const NEW_PROMPT_ID = 'new'

/**
 * Marks "open this prompt with the editor already up".
 *
 * A query flag rather than a `/edit` child route: read and edit are one page
 * (the prompt IS its body), so this says which MODE that page opens in, not
 * which page to open. The detail routes carry `meta.ownsQuery`, so toggling it
 * re-renders the page instead of remounting the routed view — which would
 * destroy the form and the draft in it.
 */
export const EDIT_QUERY = { edit: '1' } as const

type PromptTree = keyof typeof PROMPT_ROUTES

/** Which tree a route belongs to, read from its own name. */
function treeOfRoute(routeName: RouteRecordName | null | undefined): PromptTree | null {
  const name = typeof routeName === 'string' ? routeName : ''
  if (name.startsWith('qms-')) return 'qms'
  if (name.startsWith('products-')) return 'td'
  return null
}

/** Which tree a project belongs to. `regulatory_monitoring` has no chat page. */
export function treeOfProject(project: Pick<Project, 'type'> | null | undefined): PromptTree | null {
  if (project?.type === 'qms') return 'qms'
  if (project?.type === 'td') return 'td'
  return null
}

/** The prompts list, from anywhere inside the same project tree. */
export function promptsListRoute(
  routeName: RouteRecordName | null | undefined,
  pid: string,
): RouteLocationRaw {
  const tree = treeOfRoute(routeName) ?? 'qms'
  return { name: PROMPT_ROUTES[tree].list, params: { pid } }
}

/** One prompt, or `NEW_PROMPT_ID` for the create form. */
export function promptDetailRoute(
  routeName: RouteRecordName | null | undefined,
  pid: string,
  promptId: string,
  { edit = false }: { edit?: boolean } = {},
): RouteLocationRaw {
  const tree = treeOfRoute(routeName) ?? 'qms'
  return {
    name: PROMPT_ROUTES[tree].detail,
    params: { pid, promptId },
    ...(edit ? { query: { ...EDIT_QUERY } } : {}),
  }
}

/** Back to the conversation the prompts belong to. */
export function promptsChatRoute(
  routeName: RouteRecordName | null | undefined,
  pid: string,
): RouteLocationRaw {
  const tree = treeOfRoute(routeName) ?? 'qms'
  return { name: PROMPT_ROUTES[tree].chat, params: { pid } }
}

/**
 * The prompts list for a project, by its TYPE — what the global chat dock needs,
 * since it renders on every route and cannot read the tree off its own name.
 * Null when the project type has no chat page.
 */
export function promptsListRouteForProject(
  project: Pick<Project, 'id' | 'type'> | null | undefined,
): RouteLocationRaw | null {
  const tree = treeOfProject(project)
  if (!tree || !project) return null
  return { name: PROMPT_ROUTES[tree].list, params: { pid: project.id } }
}

/** One prompt, by project type — same reason as above. */
export function promptDetailRouteForProject(
  project: Pick<Project, 'id' | 'type'> | null | undefined,
  promptId: string,
): RouteLocationRaw | null {
  const tree = treeOfProject(project)
  if (!tree || !project) return null
  return { name: PROMPT_ROUTES[tree].detail, params: { pid: project.id, promptId } }
}
